/**
 * The credits panel (mission 9 WP13 T080; sheet 50 callout 5; B.2, B.3, B.4; contracts/ui-api.md
 * "Sheet 50"; squad U17).
 *
 * Available is the top-bar chip's figure: the credits read (`['credits']`, the key the chip observes, so
 * no extra request) and its `balance`. It is split by the same read's `billing` member into included this
 * cycle and purchased (`included_remaining`, `purchased_remaining`); a mission 3 body without the member,
 * or a credits read still loading, takes the split and the balance from the billing read, which carries
 * the same members (U17). `creditsIncludedNote` states the order of use. States: `split`, `zero`.
 *
 * `children` are the soft-cap status and the blocks table, placed inside the card by `Billing`.
 */
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { BillingView } from '@/lib/api/billing'
import { Mono, count } from '@/lib/format'
import { copyText, type TextKey } from '@/lib/provisional-copy'
import { creditsQuery } from '@/lib/queries'

export interface CreditsPanelProps {
  view: BillingView
  children?: ReactNode
}

export function CreditsPanel({ view, children }: CreditsPanelProps) {
  const { data: credits } = useQuery(creditsQuery())
  const available = credits?.balance ?? view.credits.balance
  const split = credits?.billing ?? view.credits
  const figures: Array<[TextKey, number]> = [
    ['creditsAvailable', available],
    ['creditsIncluded', split.included_remaining],
    ['creditsPurchased', split.purchased_remaining],
  ]
  return (
    <Card>
      <CardHeader>
        <CardTitle>{copyText('creditsPanelTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="grid grid-cols-3 gap-4">
          {figures.map(([label, value]) => (
            <div key={label} className="flex flex-col gap-1">
              <dt className="text-body-sm text-navy-600">{copyText(label)}</dt>
              <dd className="text-h3 text-navy-950">
                <Mono>{count(value)}</Mono>
              </dd>
            </div>
          ))}
        </dl>
        <p className="text-body-sm text-navy-600">{copyText('creditsIncludedNote')}</p>
        {children}
      </CardContent>
    </Card>
  )
}
